import { BonusBalanceInfo } from '@@types'
import { gql, useLazyQuery, useQuery, type WatchQueryFetchPolicy } from '@apollo/client'

/**
 * GraphQL запрос для получения баланса бонусов пользователя
 * Поля скопированы из SHORTCUT_LOYALTY_BONUS_INFO_FIELDS в customer-app
 */
export const GQL_QUERY_MY_BONUS_TOTAL = gql`
  query myBonus {
    myBonus {
      haveBonusCard
      bonusBalance
      bonusCards {
        id
        cardNumber
        barCode
        status
        image {
          url
        }
        title
      }
      activeBonuses
      pendingBonuses {
        amount
        activationDate
      }
      nearestExpiringBonus {
        expireAmount
        expireAt
      }
      loyaltyLevelInfo {
        name
        saleAmount
        returnAmount
        nextLevel {
          name
          needToNextLevel
          nextCalcDate
        }
      }
      tokenAccounts {
        id
        name
        nameCases {
          nominative
          genitive
          plural
          abbreviation
        }
        totalBalance
        expireAmount
        expirationAt
      }
      accumulations {
        saleAmount
        returnAmount
        purchaseCount
      }
    }
  }
`

type MyBonusTotalQueryResult = {
  myBonus: BonusBalanceInfo | null
}

export interface UseFetchMyBonusTotalOptions {
  skip?: boolean
  fetchPolicy?: WatchQueryFetchPolicy
}

export const useFetchMyBonusTotal = (options?: UseFetchMyBonusTotalOptions) => {
  const { data, loading, error, refetch } = useQuery<MyBonusTotalQueryResult>(
    GQL_QUERY_MY_BONUS_TOTAL,
    {
      skip: options?.skip,
      fetchPolicy: options?.fetchPolicy ?? 'cache-and-network',
    }
  )

  const bonus = data?.myBonus ?? null

  return {
    data,
    bonus,
    loading,
    error,
    refetch,
  }
}

export type { MyBonusTotalQueryResult }

export const useLazyFetchMyBonusTotal = (options?: UseFetchMyBonusTotalOptions) => {
  const [ loadQuery, { data, loading, error, refetch } ] = useLazyQuery<MyBonusTotalQueryResult>(GQL_QUERY_MY_BONUS_TOTAL, {
    fetchPolicy: options?.fetchPolicy ?? 'cache-and-network'
  })

  /**
   * Обёртка над load, которая проверяет условия перед запросом
   * @param shouldSkip - если true, запрос не будет выполнен
   */
  const load = async (shouldSkip?: boolean) => {
    if (shouldSkip) {
      return { data: undefined }
    }
    return loadQuery()
  }

  return {
    load,
    data,
    loading,
    error,
    refetch
  }
}